import React, { ChangeEvent, useEffect, useState } from 'react';
import { Checkbox, Flex, Input, message, Modal } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { getBatchRejectSelections, getRejectReason } from '@/api/contentReview';

interface RejectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    image: number[],
    title: number[],
    description: number[],
    remark: string
  ) => Promise<void>;
}

const RejectDialog: React.FC<RejectDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [remarkChecked, setRemarkChecked] = useState<boolean>(false);
  const [textValue, setTextValue] = useState<string>('');
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectImageKeys, setSelectImageKeys] = useState<number[]>([]);
  const [selectTitleKeys, setSelectTitleKeys] = useState<number[]>([]);
  const [selectDescriptionKeys, setSelectDescriptionKeys] = useState<number[]>(
    []
  );
  const [defaultsInitialized, setDefaultsInitialized] = useState<boolean>(false);

  const { data: rejectReason } = useQuery({
    queryKey: ['rejectReason'],
    queryFn: async () => {
      const resp = await getRejectReason();
      if (resp.status !== 200) {
        return [];
      }
      const data = resp.data.map((item) => ({
        label: item.dictDesc,
        value: item.dictKey,
      }));
      return data;
    },
  });

  const { data: batchRejectSelections } = useQuery({
    queryKey: ['batchRejectSelections'],
    queryFn: async () => {
      const resp = await getBatchRejectSelections();
      if (resp.status !== 200) {
        return [];
      }
      const selections = resp.data.map((item) => ({
        label: item.dictDesc,
        value: item.dictKey,
      }));
      return selections;
    },
    refetchOnWindowFocus: false,
  });

  // 弹窗打开时，预选 Image violation + 第一个子选项（Pornography and adult content）
  useEffect(() => {
    if (isOpen && rejectReason && batchRejectSelections && !defaultsInitialized) {
      const imageKey = rejectReason[0]?.value;
      const firstSubKey = batchRejectSelections[0]?.value;
      if (imageKey && firstSubKey) {
        setSelectImageKeys([imageKey, firstSubKey]);
      }
      setDefaultsInitialized(true);
    }
    if (!isOpen) {
      setDefaultsInitialized(false);
    }
  }, [isOpen, rejectReason, batchRejectSelections, defaultsInitialized]);

  const handleImagesChange = (checkedValue: number[]) => {
    const imageReasonKey = rejectReason?.[0].value;
    if (!imageReasonKey) {
      return;
    }
    if (checkedValue.length === 1 && checkedValue[0] === imageReasonKey) {
      setSelectImageKeys([]);
    } else {
      const _checkedValue = new Set([...checkedValue, imageReasonKey]);
      setSelectImageKeys([..._checkedValue]);
    }
  };

  const handleTitleChange = (checkedValue: number[]) => {
    const titleReasonKey = rejectReason?.[1].value;
    if (!titleReasonKey) {
      return;
    }
    if (checkedValue.length === 1 && checkedValue[0] === titleReasonKey) {
      setSelectTitleKeys([]);
    } else {
      const _checkedValue = new Set([...checkedValue, titleReasonKey]);
      setSelectTitleKeys([..._checkedValue]);
    }
  };

  const handleDescriptionChange = (checkedValue: number[]) => {
    const descriptionReasonKey = rejectReason?.[2].value;
    if (!descriptionReasonKey) {
      return;
    }
    if (checkedValue.length === 1 && checkedValue[0] === descriptionReasonKey) {
      setSelectDescriptionKeys([]);
    } else {
      const _checkedValue = new Set([...checkedValue, descriptionReasonKey]);
      setSelectDescriptionKeys([..._checkedValue]);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setTextValue(e.target.value);
  };

  const handleOk = async () => {
    if (
      selectImageKeys.length === 0 &&
      selectTitleKeys.length === 0 &&
      selectDescriptionKeys.length === 0
    ) {
      messageApi.warning('Please select a reason');
      return;
    }
    if (remarkChecked && textValue.length === 0) {
      messageApi.warning('Please provide additional comments.');
      return;
    }
    setLoading(true);
    await onConfirm(
      selectImageKeys,
      selectTitleKeys,
      selectDescriptionKeys,
      textValue
    );
    setLoading(false);
    onClose();
  };

  const handleCancel = () => {
    setRemarkChecked(false);
    setTextValue('');
    setSelectImageKeys([]);
    setSelectTitleKeys([]);
    setSelectDescriptionKeys([]);
    onClose();
  };

  return (
    <>
      {contextHolder}
      <Modal
        open={isOpen}
        title="Please choose the reason why this artwork has been rejected in below"
        width={800}
        onCancel={handleCancel}
        onOk={handleOk}
        destroyOnClose
        confirmLoading={loading}
      >
        <Flex style={{ width: '100%', flexWrap: 'nowrap', columnGap: 0 }}>
          <Checkbox.Group
            value={selectImageKeys}
            style={{
              width: '33%',
              flexDirection: 'column',
            }}
            onChange={(checkedValue: number[]) =>
              handleImagesChange(checkedValue)
            }
          >
            <Checkbox value={rejectReason?.[0].value}>
              <span style={{ fontWeight: 600 }}>{rejectReason?.[0].label}</span>
            </Checkbox>
            {batchRejectSelections?.map((selection) => (
              <Checkbox key={selection.value} value={selection.value}>
                {selection.label}
              </Checkbox>
            ))}
          </Checkbox.Group>
          <Checkbox.Group
            value={selectTitleKeys}
            style={{
              width: '33%',
              flexDirection: 'column',
            }}
            onChange={(checkedValue: number[]) =>
              handleTitleChange(checkedValue)
            }
          >
            <Checkbox value={rejectReason?.[1].value}>
              <span style={{ fontWeight: 600 }}>{rejectReason?.[1].label}</span>
            </Checkbox>
            {batchRejectSelections?.map((selection) => (
              <Checkbox key={selection.value} value={selection.value}>
                {selection.label}
              </Checkbox>
            ))}
          </Checkbox.Group>
          <Checkbox.Group
            value={selectDescriptionKeys}
            style={{ width: '33%', flexDirection: 'column' }}
            onChange={(checkedValue: number[]) =>
              handleDescriptionChange(checkedValue)
            }
          >
            <Checkbox value={rejectReason?.[2].value}>
              <span style={{ fontWeight: 600 }}>{rejectReason?.[2].label}</span>
            </Checkbox>
            {batchRejectSelections?.map((selection) => (
              <Checkbox key={selection.value} value={selection.value}>
                {selection.label}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </Flex>
        <Flex style={{ marginTop: 20, alignItems: 'flex-start' }}>
          <Checkbox onChange={(e) => setRemarkChecked(e.target.checked)}>
            Remark
          </Checkbox>
          <Input.TextArea
            placeholder="Please provide additional comments."
            rows={4}
            maxLength={6}
            disabled={!remarkChecked}
            value={textValue}
            onChange={handleInputChange}
            allowClear
          />
        </Flex>
      </Modal>
    </>
  );
};

export default RejectDialog;
